import React, { forwardRef, useCallback, useEffect, useContext, useImperativeHandle, useReducer, useRef, useState, PropsWithChildren } from 'react'
import cn from 'clsx'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import ReactModal from 'react-modal'
import styled, { keyframes } from 'styled-components'
import { REACT_MODAL_TRANSITION_DURATION } from 'styles/reactModal'
import { LuxkitModalBehaviourDispatchContext, LuxkitModalBehaviourStateContext } from './context/LuxkitModalBehaviourContexts'
import { luxkitModalBehaviourStateReducer } from './context/LuxkitModalBehaviourState'
import { LUXKIT_MODAL_HEADER_SUBTITLE_ID, LUXKIT_MODAL_HEADER_TITLE_ID } from './ModalHeader'
import { ModalElementContext } from 'contexts/ModalProvider'

const MODAL_HEADER_GRID_AREA: string = 'modal-header'
const MODAL_BODY_GRID_AREA: string = 'modal-body'
const MODAL_FOOTER_GRID_AREA: string = 'modal-footer'

const ModalOverlay = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: end;
  backdrop-filter: blur(2px);
  max-height: calc(var(--vh, 1vh) * 100);
  overflow-y: unset !important; /* this is only here until the legacy modal is ditched */

  ${mediaQueryUp.sm} {
    align-items: center;
    justify-content: center;
    && {
      transition-property: opacity, background-color;
    }
  }

  &::-webkit-scrollbar {
    display: none;
  }
`

export const ModalSlideUp = keyframes`
  from {
    transform: translateY(100%);
  }

  to {
    transform: translateY(0);
  }
`

export const ModalSlideDown = keyframes`
  from {
    transform: translateY(0);
  }

  to {
    transform: translateY(100%);
  }
`

const ModalElement = styled(ReactModal)`
  position: relative;
  overflow: hidden;
  background-color: ${props => props.theme.palette.neutral.eight};
  border-radius: ${props => props.theme.borderRadius.large} ${props => props.theme.borderRadius.large} 0 0;
  box-shadow: ${props => props.theme.shadow.top.medium};

  min-height: ${rem(54)};
  max-height: calc(100% - ${rem(72)});
  height: auto;
  width: 100%;

  animation-name: ${ModalSlideDown};
  animation-iteration-count: 1;
  animation-duration: 300ms;
  animation-fill-mode: forwards;

  ${mediaQueryDown.xs} {
    &.is-full-height {
      border-radius: 0;
      animation: none;
      max-height: unset;
      height: 100%;

      &.is-open {
        animation: none;
      }
    }
  }

  &.is-max-height {
    height: 100%;
  }

  &.is-open {
    animation-name: ${ModalSlideUp};
  }

  ${mediaQueryUp.sm} {
    animation: none;
    border-radius: ${props => props.theme.borderRadius.large};
    box-shadow: ${props => props.theme.shadow.bottom.medium};

    min-height: ${rem(132)};
    max-height: 90%;

    &.is-small {
      max-width: ${rem(560)};
    }

    &.is-large {
      max-width: ${rem(720)};
    }

    &.is-open {
      animation: none;
    }

    &.is-full-height {
      height: 100%;
    }
  }

  &,
  > form {
    display: grid;
    grid-template-areas:
      '${MODAL_HEADER_GRID_AREA}'
      '${MODAL_BODY_GRID_AREA}'
      '${MODAL_FOOTER_GRID_AREA}';
    grid-template-rows:
      [first]
      min-content
      1fr
      min-content
      [last];
    grid-template-columns: [first] 1fr [last];

    > .modal-header {
      grid-area: ${MODAL_HEADER_GRID_AREA};
    }

    > .modal-body {
      grid-area: ${MODAL_BODY_GRID_AREA};
    }

    > .modal-footer {
      grid-area: ${MODAL_FOOTER_GRID_AREA};
    }
  }

  > form {
    /* stretch the form over the grid */
    grid-column: first/last;
    grid-row: first/last;
  }
`

export const MODAL_BASE_SIZES = ['small', 'large'] as const
export const MODAL_BASE_DEFAULT_SIZE = MODAL_BASE_SIZES[0]

export interface ModalBaseProps {
  onAfterOpen?: () => void;
  className?: string
  isFullHeight?: boolean
  isMaxHeight?: boolean
  isOpen?: boolean
  size?: typeof MODAL_BASE_SIZES[number]
  onAfterClose?: () => void
  onClose?: () => void
}

const ModalBase = forwardRef<HTMLDivElement, PropsWithChildren<ModalBaseProps>>((props, ref) => {
  const {
    children,
    className,
    isFullHeight,
    isMaxHeight,
    isOpen,
    size = MODAL_BASE_DEFAULT_SIZE,
    onAfterClose,
    onAfterOpen,
    onClose,
  } = props

  const [state, dispatch] = useReducer(luxkitModalBehaviourStateReducer, {})
  const elementContext = useContext(ModalElementContext)
  const baseRef = useRef<HTMLDivElement>()
  const [hasOpened, setHasOpened] = useState(false)

  const actuallyOpen = typeof isOpen === 'undefined' ? elementContext?.open : isOpen

  useEffect(() => { setHasOpened(false) }, [actuallyOpen])

  const afterOpen = useCallback(() => {
    setHasOpened(true)

    onAfterOpen?.()
  }, [setHasOpened, onAfterOpen])

  useImperativeHandle(ref, () => baseRef.current!)

  const handleClose = useCallback((event) => {
    event.stopPropagation()
    if (onClose && hasOpened) {
      onClose()
    } else {
      elementContext?.resolve({})
    }
  }, [elementContext, onClose, hasOpened])

  const afterClose = useCallback(() => {
    // if an onclose is provided this is being manually managed, don't rely on our element context
    if (!onClose) {
      elementContext?.afterClose()
    }
    onAfterClose?.()
  }, [elementContext, onAfterClose, onClose])

  return <ModalElement
    aria={{
      labelledby: LUXKIT_MODAL_HEADER_TITLE_ID,
      describedby: LUXKIT_MODAL_HEADER_SUBTITLE_ID,
    }}
    overlayElement={(props, content) => <ModalOverlay {...props}>{content}</ModalOverlay>}
    overlayClassName="luxkit-modal__overlay"
    className={cn(
      className,
      'luxkit-modal__content',
      `is-${size}`,
      {
        'is-full-height': isFullHeight,
        'is-max-height': isMaxHeight,
        'is-open': actuallyOpen,
      },
    )}
    closeTimeoutMS={REACT_MODAL_TRANSITION_DURATION}
    contentRef={(node) => { baseRef.current = node }}
    isOpen={actuallyOpen}
    shouldCloseOnEsc
    shouldReturnFocusAfterClose={false}
    onAfterClose={afterClose}
    onRequestClose={handleClose}
    onAfterOpen={afterOpen}
  >
    <LuxkitModalBehaviourStateContext.Provider value={state}>
      <LuxkitModalBehaviourDispatchContext.Provider value={dispatch}>
        {children}
      </LuxkitModalBehaviourDispatchContext.Provider>
    </LuxkitModalBehaviourStateContext.Provider>
  </ModalElement>
})

export default ModalBase
